import {type FormEvent, type ReactNode, useState} from "react";

import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Loader2Icon, MoreHorizontalIcon, PencilIcon, PlusIcon, Trash2Icon} from "lucide-react";
import {toast} from "sonner";

import {type Plan, plansCreate, plansDelete, plansList, plansUpdate} from "@/sdk";

import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import {Card, CardContent} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {formatPrice} from "@/lib/format";

const QK = ["admin", "plans"] as const;

type PlanForm = {
  name: string;
  slug: string;
  description: string;
  vcpus: string;
  memory_mb: string;
  disk_gb: string;
  bandwidth_gb: string;
  price_cents: string;
  currency: string;
  is_active: boolean;
};

const empty: PlanForm = {
  name: "",
  slug: "",
  description: "",
  vcpus: "1",
  memory_mb: "2048",
  disk_gb: "20",
  bandwidth_gb: "1000",
  price_cents: "500",
  currency: "USD",
  is_active: true,
};

export const AdminPlans = () => {
  const qc = useQueryClient();
  const [openForm, setOpenForm] = useState<{mode: "create" | "edit"; plan?: Plan} | null>(null);
  const [form, setForm] = useState<PlanForm>(empty);
  const [deleteTarget, setDeleteTarget] = useState<Plan | null>(null);

  const query = useQuery({
    queryKey: QK,
    queryFn: async () => {
      const {data, error} = await plansList();
      if (error) throw error;
      return (data?.items ?? []).slice().sort((a, b) => a.display_order - b.display_order);
    },
  });

  const invalidate = () => qc.invalidateQueries({queryKey: QK});

  const num = (s: string): number => {
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  const createM = useMutation({
    mutationFn: () =>
      plansCreate({
        body: {
          name: form.name,
          slug: form.slug,
          description: form.description || undefined,
          vcpus: num(form.vcpus),
          memory_mb: num(form.memory_mb),
          disk_gb: num(form.disk_gb),
          bandwidth_gb: num(form.bandwidth_gb),
          price_cents: num(form.price_cents),
          currency: form.currency || "USD",
          is_active: form.is_active,
        },
      }),
    onSuccess: ({error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      toast.success("Plan created");
      setOpenForm(null);
      invalidate();
    },
  });

  const updateM = useMutation({
    mutationFn: () =>
      plansUpdate({
        path: {id: openForm!.plan!.id},
        body: {
          name: form.name || undefined,
          description: form.description || undefined,
          vcpus: num(form.vcpus),
          memory_mb: num(form.memory_mb),
          disk_gb: num(form.disk_gb),
          bandwidth_gb: num(form.bandwidth_gb),
          price_cents: num(form.price_cents),
          currency: form.currency || undefined,
          is_active: form.is_active,
        },
      }),
    onSuccess: ({error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      toast.success("Plan updated");
      setOpenForm(null);
      invalidate();
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => plansDelete({path: {id}}),
    onSuccess: ({error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      toast.success("Plan deleted");
      setDeleteTarget(null);
      invalidate();
    },
  });

  const startCreate = () => {
    setForm(empty);
    setOpenForm({mode: "create"});
  };

  const startEdit = (plan: Plan) => {
    setForm({
      name: plan.name,
      slug: plan.slug,
      description: plan.description ?? "",
      vcpus: String(plan.vcpus),
      memory_mb: String(plan.memory_mb),
      disk_gb: String(plan.disk_gb),
      bandwidth_gb: String(plan.bandwidth_gb ?? 0),
      price_cents: String(plan.price_cents),
      currency: plan.currency,
      is_active: plan.is_active,
    });
    setOpenForm({mode: "edit", plan});
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (openForm?.mode === "create") createM.mutate();
    else updateM.mutate();
  };

  const plans = query.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Plans</h1>
          <p className="text-sm text-muted-foreground">
            VM plans visible to customers when creating servers.
          </p>
        </div>
        <Button onClick={startCreate}>
          <PlusIcon /> Add plan
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="flex items-center justify-center p-12 text-muted-foreground">
              <Loader2Icon className="size-5 animate-spin" />
            </div>
          ) : query.isError ? (
            <div className="p-6 text-sm text-destructive">Failed to load plans.</div>
          ) : plans.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No plans defined.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan</TableHead>
                  <TableHead>Resources</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="font-medium">{p.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{p.slug}</div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {p.vcpus} vCPU · {p.memory_mb} MB · {p.disk_gb} GB
                    </TableCell>
                    <TableCell>{formatPrice(p.price_cents, p.currency)}</TableCell>
                    <TableCell>
                      {p.is_active ? (
                        <Badge variant="success">active</Badge>
                      ) : (
                        <Badge variant="muted">inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm">
                            <MoreHorizontalIcon />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => startEdit(p)}>
                            <PencilIcon /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            variant="destructive"
                            onSelect={() => setDeleteTarget(p)}
                          >
                            <Trash2Icon /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={openForm !== null} onOpenChange={(o) => !o && setOpenForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {openForm?.mode === "create" ? "Create plan" : "Edit plan"}
            </DialogTitle>
            <DialogDescription>
              Specs and price for the plan. Slug is immutable after creation.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field id="name" label="Name">
                <Input
                  id="name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                />
              </Field>
              <Field id="slug" label="Slug">
                <Input
                  id="slug"
                  required
                  disabled={openForm?.mode === "edit"}
                  value={form.slug}
                  onChange={(e) => setForm({...form, slug: e.target.value})}
                />
              </Field>
            </div>
            <Field id="description" label="Description">
              <Input
                id="description"
                value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field id="vcpus" label="vCPUs">
                <Input
                  id="vcpus"
                  type="number"
                  min={1}
                  value={form.vcpus}
                  onChange={(e) => setForm({...form, vcpus: e.target.value})}
                />
              </Field>
              <Field id="memory_mb" label="Memory (MB)">
                <Input
                  id="memory_mb"
                  type="number"
                  min={128}
                  value={form.memory_mb}
                  onChange={(e) => setForm({...form, memory_mb: e.target.value})}
                />
              </Field>
              <Field id="disk_gb" label="Disk (GB)">
                <Input
                  id="disk_gb"
                  type="number"
                  min={1}
                  value={form.disk_gb}
                  onChange={(e) => setForm({...form, disk_gb: e.target.value})}
                />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field id="bandwidth_gb" label="Bandwidth (GB/mo)">
                <Input
                  id="bandwidth_gb"
                  type="number"
                  min={0}
                  value={form.bandwidth_gb}
                  onChange={(e) => setForm({...form, bandwidth_gb: e.target.value})}
                />
              </Field>
              <Field id="price_cents" label="Price (cents)">
                <Input
                  id="price_cents"
                  type="number"
                  min={0}
                  value={form.price_cents}
                  onChange={(e) => setForm({...form, price_cents: e.target.value})}
                />
              </Field>
              <Field id="currency" label="Currency">
                <Input
                  id="currency"
                  value={form.currency}
                  maxLength={3}
                  onChange={(e) => setForm({...form, currency: e.target.value.toUpperCase()})}
                />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({...form, is_active: e.target.checked})}
              />
              Active (visible to customers)
            </label>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenForm(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createM.isPending || updateM.isPending}>
                {openForm?.mode === "create"
                  ? createM.isPending
                    ? "Creating…"
                    : "Create plan"
                  : updateM.isPending
                    ? "Saving…"
                    : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteTarget !== null} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete plan?</DialogTitle>
            <DialogDescription>
              Soft-deletes <span className="font-mono">{deleteTarget?.slug}</span>. Existing
              servers on this plan keep running.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteM.isPending}
              onClick={() => deleteTarget && deleteM.mutate(deleteTarget.id)}
            >
              {deleteM.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function Field({id, label, children}: {id: string; label: string; children: ReactNode}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

function extractError(err: unknown): string {
  return (err as {detail?: string} | undefined)?.detail ?? "Request failed";
}
